import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle } from '../components_catalyst/dialog';
import { Input } from '../components_catalyst/input';
import { Button } from '../components_catalyst/button';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import { DropdownMenu } from '../components_catalyst/dropdown';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Dropdown, DropdownButton, DropdownItem } from '../components_catalyst/dropdown';
import { Toast } from '../components_misc/Toast';
import { useShareChipsNavigation } from '../hooks/useShareChipsNavigation';
import { useSelector, useDispatch } from 'react-redux';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { RootState, AppDispatch } from '../store/store';
import { lookupUserByEmail } from '../store/map3DataSlice';
import { updateMilestoneMap, createSignUpLink } from '../store/map3PendingSlice';
import { useParams } from 'react-router-dom';
import { MinusIcon } from '@heroicons/react/24/solid';
import { DropdownDivider } from '../components_catalyst/dropdown';
import { MilestoneMap1, MapInvitation, UserWithAccessAndRole, UserProfile1 } from '../types';
import { useAuth } from '../components_auth/AuthContext';
import { sendMail } from '../services/email/sendMail';
import { RoleDropdown, Role, roleLabels } from './RoleDropdown';
import { AccessStatus } from './AccessStatus';
import { CopyLinkButton } from './CopyLink';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ShareRecipient {
  email: string;
  status: 'checking' | 'exists' | 'new';
  userName?: string;
  uid?: string;
  avatarInitials?: string;
  lastSeen?: string;
}

interface ShareChip {
  email: string;
  role: 'editor' | 'commenter' | 'viewer';
  userName?: string;
  avatarInitials?: string;
  status: 'checking' | 'exists' | 'new';
}

interface ShareChipProps {
  displayText: string;
  email: string;
  index: number;
  onRemove: (email: string) => void;
  onFocus: (index: number) => void;
  onKeyDown: (e: React.KeyboardEvent, index: number) => void;
  isFocused: boolean;
  children?: React.ReactNode;
}

interface AccessControl {
  uid: string;
  name: string;
  role: 'owner' | 'editor' | 'commenter' | 'viewer';
  entityType: 'user' | 'email' | 'org' | 'team';
  joinedDate?: string;
  lastSeen?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BUTTON_STYLES = {
  primary: 'px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-500 rounded-md cursor-pointer',
  secondary: 'px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md cursor-pointer',
} as const;

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

function getAvatarColor(email: string): string {
  // Generate a consistent color based on email
  const colors = [
    'bg-blue-600',
    'bg-purple-600',
    'bg-pink-600',
    'bg-indigo-600',
    'bg-teal-600',
    'bg-green-600',
  ];
  const hash = email.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
  return colors[hash % colors.length];
}

function ShareChip({
  displayText,
  email,
  index,
  onRemove,
  onFocus,
  onKeyDown,
  isFocused,
  children,
}: ShareChipProps) {
  return (
    <span
      data-chip-index={index}
      tabIndex={0}
      className={`inline-flex items-center gap-x-1 rounded-full px-2 py-1 text-sm text-gray-600
        ${isFocused ? 'bg-gray-200' : 'bg-gray-100'}`}
      onFocus={() => onFocus(index)}
      onKeyDown={(e) => onKeyDown(e, index)}
    >
      {children}
      {displayText}
      <button
        type="button"
        onClick={() => onRemove(email)}
        className="group -mr-1 h-4 w-4 rounded-full hover:bg-gray-200"
      >
        <span className="sr-only">Remove</span>
        <svg viewBox="0 0 14 14" className="h-4 w-4 stroke-gray-600/50 group-hover:stroke-gray-600">
          <path d="M4 4l6 6m0-6l-6 6" />
        </svg>
      </button>
    </span>
  );
}

function PeopleWithAccessForm({
  milestoneMap,
  onClose,
  onStartInvite,
}: {
  milestoneMap: MilestoneMap1 | undefined;
  onClose: () => void;
  onStartInvite: () => void;
}) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pendingRoleChanges, setPendingRoleChanges] = useState<Record<string, Role>>({});
  const [pendingInvitationRoleChanges, setPendingInvitationRoleChanges] = useState<
    Record<string, Role>
  >({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendingInvitation, setResendingInvitation] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { user: currentUser } = useAuth();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile) as UserProfile1;

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Clear error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleRoleChangeClick = (
    userOrInvitation: UserWithAccessAndRole | MapInvitation,
    newRole: Role,
  ) => {
    if ('email' in userOrInvitation) {
      // This is a MapInvitation
      setPendingInvitationRoleChanges((prev) => ({
        ...prev,
        [userOrInvitation.email]: newRole,
      }));
    } else {
      // This is a UserWithAccessAndRole
      setPendingRoleChanges((prev) => ({
        ...prev,
        [userOrInvitation.uid]: newRole,
      }));
    }
  };

  const handleRemoveAccess = async (userOrInvitation: UserWithAccessAndRole | MapInvitation) => {
    if (!milestoneMap?.uuid) return;

    try {
      let updatedUsers = milestoneMap.usersWithAccess || [];
      let updatedInvitations = milestoneMap.pendingMapInvitations || [];

      if ('email' in userOrInvitation) {
        // This is a MapInvitation
        updatedInvitations = updatedInvitations.filter(
          (invitation) => invitation.email !== userOrInvitation.email,
        );
      } else {
        // This is a UserWithAccessAndRole
        updatedUsers = updatedUsers.filter((user) => user.uid !== userOrInvitation.uid);
      }

      await dispatch(
        updateMilestoneMap({
          uuid: milestoneMap.uuid,
          updates: {
            usersWithAccess: updatedUsers,
            pendingMapInvitations: updatedInvitations,
          },
        }),
      );

      // Show appropriate success message based on the type
      setSuccessMessage(
        'email' in userOrInvitation
          ? `Invitation to ${userOrInvitation.email} has been removed`
          : `${userOrInvitation.userName}'s access has been removed`,
      );
    } catch (error) {
      console.error('Error removing access:', error);
      setErrorMessage('Failed to remove access. Please try again.');
    }
  };

  const handleResendInvitation = async (invitation: MapInvitation | UserWithAccessAndRole) => {
    if (!milestoneMap?.uuid) return;

    let inviteEmail: string | undefined;

    if ('email' in invitation) {
      // For MapInvitation, use the email directly
      inviteEmail = invitation.email;
    } else {
      // For UserWithAccessAndRole without joinedDate, find their email in pendingMapInvitations
      inviteEmail = milestoneMap.pendingMapInvitations?.find(
        (inv) => inv.uid === invitation.uid,
      )?.email;
    }

    if (!inviteEmail) {
      setErrorMessage('Cannot resend invitation - user email not found');
      return;
    }

    setResendingInvitation(inviteEmail);

    try {
      // Send the invitation email
      const result = await sendMail({
        to: inviteEmail,
        subject: `${userProfile.firstName} ${userProfile.lastName} invited you to collaborate on a milestone map`,
        text: `${userProfile.firstName} ${userProfile.lastName} has invited you to collaborate on the milestone map "${milestoneMap.title}". Click here to view: ${process.env.REACT_APP_BASE_URL}/${milestoneMap.owner.name}/maps/${milestoneMap.shortId}`,
      });

      // TODO: We could update pendingMapInvitations here with an updated
      // invitation date or a count of how many times we've sent invitations.
      // For alpha, we'll just resend the email and be done.

      // Show success message
      setSuccessMessage(`Invitation resent to ${inviteEmail}`);
    } catch (error) {
      console.error(`Error resending invitation to ${inviteEmail}:`, error);
      setErrorMessage(`Failed to resend invitation to ${inviteEmail}. Please try again later.`);
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleSave = async () => {
    if (!milestoneMap?.uuid) {
      console.error('No map UUID found');
      return;
    }

    try {
      // Create new usersWithAccess array with pending changes
      const updatedUsers = (milestoneMap.usersWithAccess || []).map((userAccessRole) => ({
        ...userAccessRole,
        role: pendingRoleChanges[userAccessRole.uid] || userAccessRole.role,
      }));

      // Create updated invitations array with pending role changes
      const updatedInvitations = (milestoneMap.pendingMapInvitations || []).map((invitation) => ({
        ...invitation,
        role: pendingInvitationRoleChanges[invitation.email] || invitation.role,
      }));

      // Dispatch the changes
      await dispatch(
        updateMilestoneMap({
          uuid: milestoneMap.uuid,
          updates: {
            usersWithAccess: updatedUsers,
            pendingMapInvitations: updatedInvitations,
          },
        }),
      );
      onClose();
    } catch (error) {
      console.error('Error updating access controls:', error);
      setErrorMessage('Failed to update access controls. Please try again.');
    }
  };

  const handleCancel = () => {
    setPendingRoleChanges({});
    setPendingInvitationRoleChanges({});
    onClose();
  };

  const hasChanges =
    Object.keys(pendingRoleChanges).length > 0 ||
    Object.keys(pendingInvitationRoleChanges).length > 0;

  return (
    <div className="space-y-6">
      {/* Success Message Toast */}
      <Toast
        message={successMessage}
        variant="info"
        position="top-right"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Error Message Toast */}
      {errorMessage && (
        <Toast
          message={errorMessage}
          variant="error"
          position="top-right"
          onClose={() => setErrorMessage(null)}
        />
      )}

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-medium text-gray-900">People with access</h3>
        <ul className="mt-4 space-y-4">
          {/* Show owner first */}
          {milestoneMap?.owner && (
            <li className="flex items-center justify-between p-2 hover:bg-gray-100">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-slate-500 text-white flex items-center justify-center">
                  {milestoneMap.owner.name[0]?.toUpperCase()}
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{milestoneMap.owner.name}</p>
                  <p className="text-sm text-gray-500">Owner</p>
                </div>
              </div>
              <span className="text-sm text-gray-400">Owner</span>
            </li>
          )}

          {/* Show other users with access */}
          {milestoneMap?.usersWithAccess?.map((user) => (
            <li
              key={user.uid}
              onClick={() => setSelectedUserId(user.uid)}
              className={`flex items-center justify-between p-2 hover:bg-gray-100
                ${selectedUserId === user.uid ? 'bg-gray-100' : ''}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-slate-500 text-white flex items-center justify-center">
                  {user.userName[0]?.toUpperCase()}
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">{user.userName}</p>
                  <AccessStatus
                    userOrInvitation={user}
                    resendingInvitation={resendingInvitation}
                    onResendInvitation={handleResendInvitation}
                  />
                </div>
              </div>
              {/* Show role selector for all users */}
              <RoleDropdown
                currentRole={pendingRoleChanges[user.uid] || user.role}
                onRoleChange={(role) => handleRoleChangeClick(user, role)}
                showRemoveOption
                onRemove={() => handleRemoveAccess(user)}
              />
            </li>
          ))}

          {/* Show pending invitations only for users not in usersWithAccess */}
          {milestoneMap?.pendingMapInvitations
            ?.filter(
              (invitation) =>
                !milestoneMap.usersWithAccess?.some((user) => user.uid === invitation.uid),
            )
            .map((invitation) => (
              <li
                key={invitation.email}
                className="flex items-center justify-between p-2 hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center">
                    {invitation.email[0]?.toUpperCase()}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{invitation.email}</p>
                    <AccessStatus
                      userOrInvitation={invitation}
                      resendingInvitation={resendingInvitation}
                      onResendInvitation={handleResendInvitation}
                    />
                  </div>
                </div>
                <RoleDropdown
                  currentRole={pendingInvitationRoleChanges[invitation.email] || invitation.role}
                  onRoleChange={(role) => handleRoleChangeClick(invitation, role)}
                  showRemoveOption
                  onRemove={() => handleRemoveAccess(invitation)}
                />
              </li>
            ))}
        </ul>
      </div>

      <div className="flex justify-between items-center">
        <CopyLinkButton
          onClick={() => navigator.clipboard.writeText(window.location.href)}
          showText={true}
        />
        <div className="flex gap-3">
          {hasChanges ? (
            <>
              <button onClick={handleCancel} className={BUTTON_STYLES.secondary}>
                Cancel
              </button>
              <button onClick={handleSave} className={BUTTON_STYLES.primary}>
                Save
              </button>
            </>
          ) : (
            <button onClick={onClose} className={BUTTON_STYLES.primary}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface EmailInputSectionProps {
  recipients: ShareRecipient[];
  emailInput: string;
  onEmailInputChange: (value: string) => void;
  onAddEmail: (email: string) => void;
  onRemoveEmail: (email: string) => void;
  handleChipFocus: (index: number) => void;
  handleChipKeyDown: (e: React.KeyboardEvent, index: number) => void;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  focusedChip: { index: number } | null;
}

function EmailInputSection({
  recipients,
  emailInput,
  onEmailInputChange,
  onAddEmail,
  onRemoveEmail,
  handleChipFocus,
  handleChipKeyDown,
  handleInputKeyDown,
  focusedChip,
}: EmailInputSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when the dialog opens
  useEffect(() => {
    // Use a slight delay to ensure the dialog has finished rendering
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => clearTimeout(focusTimer);
  }, []);

  const handleCombinedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle email addition (from original handler)
    if ((e.key === 'Enter' || e.key === ',') && emailInput.trim()) {
      e.preventDefault();
      const email = emailInput.trim().replace(/,$/, '');
      if (isValidEmail(email)) {
        onAddEmail(email);
        onEmailInputChange('');
        // Maintain focus on the input after adding email
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
    }

    // Handle navigation and deletion (from useShareChipsNavigation)
    handleInputKeyDown(e);
  };

  return (
    <div className="flex-1">
      <div className="min-h-[40px] w-full border border-slate-400 rounded-md px-3 py-2">
        <div className="flex flex-wrap gap-2 items-center">
          {recipients.map((recipient, index) => (
            <ShareChip
              key={recipient.email}
              email={recipient.email}
              displayText={
                recipient.status === 'exists'
                  ? recipient.userName || recipient.email
                  : recipient.email
              }
              index={index}
              onRemove={onRemoveEmail}
              onFocus={handleChipFocus}
              onKeyDown={(e) => handleChipKeyDown(e, index)}
              isFocused={focusedChip?.index === index}
            >
              {recipient.status === 'checking' && (
                <span className="mr-1 text-sm text-gray-500">(checking...)</span>
              )}
              {recipient.status === 'exists' && recipient.avatarInitials && (
                <span
                  className={`mr-1 w-6 h-6 rounded-full ${getAvatarColor(
                    recipient.email,
                  )} text-white flex items-center justify-center text-sm`}
                >
                  {recipient.avatarInitials}
                </span>
              )}
            </ShareChip>
          ))}
          <input
            ref={inputRef}
            type="email"
            placeholder="Add people by email"
            value={emailInput}
            onChange={(e) => onEmailInputChange(e.target.value)}
            onKeyDown={handleCombinedKeyDown}
            className="flex-1 border-0 bg-transparent p-0 placeholder:text-gray-400 focus:ring-0 min-w-[200px]"
          />
        </div>
      </div>
    </div>
  );
}

interface InviteFormProps {
  recipients: ShareRecipient[];
  emailInput: string;
  selectedRole: Role;
  notifyPeople: boolean;
  message: string;
  containerRef: React.RefObject<HTMLDivElement>;
  onEmailInputChange: (value: string) => void;
  onAddEmail: (email: string) => void;
  onRemoveEmail: (email: string) => void;
  onRoleChange: (role: Role) => void;
  onNotifyChange: (notify: boolean) => void;
  onMessageChange: (message: string) => void;
  onSend: () => void;
  onClose: () => void;
  handleChipFocus: (index: number) => void;
  handleChipKeyDown: (e: React.KeyboardEvent, index: number) => void;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  focusedChip: { index: number } | null;
}

function InviteForm({
  recipients,
  emailInput,
  selectedRole,
  notifyPeople,
  message,
  containerRef,
  onEmailInputChange,
  onAddEmail,
  onRemoveEmail,
  onRoleChange,
  onNotifyChange,
  onMessageChange,
  onSend,
  onClose,
  handleChipFocus,
  handleChipKeyDown,
  handleInputKeyDown,
  focusedChip,
}: InviteFormProps) {
  return (
    <div className="space-y-6">
      <div className="mt-4 flex gap-3">
        <EmailInputSection
          recipients={recipients}
          emailInput={emailInput}
          onEmailInputChange={onEmailInputChange}
          onAddEmail={onAddEmail}
          onRemoveEmail={onRemoveEmail}
          handleChipFocus={handleChipFocus}
          handleChipKeyDown={handleChipKeyDown}
          handleInputKeyDown={handleInputKeyDown}
          focusedChip={focusedChip}
        />

        {/* Role selector dropdown */}
        <RoleDropdown
          currentRole={selectedRole}
          onRoleChange={onRoleChange}
          disabled={recipients.length === 0}
          variant="form"
        />
      </div>

      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={notifyPeople}
            onChange={(e) => onNotifyChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Notify people</span>
        </label>
      </div>

      {notifyPeople && (
        <div>
          <textarea
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder="Add a message (optional)"
            className="w-full rounded-md border border-slate-400 px-3 py-2 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            rows={3}
          />
        </div>
      )}

      <div className="flex justify-between items-center">
        <CopyLinkButton
          onClick={() => navigator.clipboard.writeText(window.location.href)}
          showText={false}
        />
        <div className="flex gap-3">
          <button onClick={onClose} className={BUTTON_STYLES.secondary}>
            Cancel
          </button>
          <button onClick={onSend} className={BUTTON_STYLES.primary}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export function ShareDialog({ open, onClose }: ShareDialogProps) {
  const [isInviteMode, setIsInviteMode] = useState(false);
  const isPeopleWithAccessMode = !isInviteMode;
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const mapShortId = params.mapShortId;
  const containerRef = useRef<HTMLDivElement>(null);
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((m: MilestoneMap1) => m.shortId === mapShortId),
  );
  const { user } = useAuth();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  // State for tracking invitation process
  const [invitationSuccess, setInvitationSuccess] = useState<string | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationsInProgress, setInvitationsInProgress] = useState<string[]>([]);
  const [signupLinksInProgress, setSignupLinksInProgress] = useState<string[]>([]);
  const [signupLinksSent, setSignupLinksSent] = useState<{ email: string; success: boolean }[]>([]);

  // Local state
  const [recipients, setRecipients] = useState<ShareRecipient[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [selectedRole, setSelectedRole] = useState<'editor' | 'commenter' | 'viewer'>('viewer');
  const [notifyPeople, setNotifyPeople] = useState(true);
  const [message, setMessage] = useState('');

  // Clear error message after 5 seconds
  useEffect(() => {
    if (invitationError) {
      const timer = setTimeout(() => {
        setInvitationError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [invitationError]);

  // Focus the input when the dialog opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure the dialog is fully rendered
      const timer = setTimeout(() => {
        const input = containerRef.current?.querySelector(
          'input[type="email"]',
        ) as HTMLInputElement;
        input?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleRemoveEmail = (email: string) => {
    const updatedRecipients = recipients.filter((r) => r.email !== email);
    setRecipients(updatedRecipients);

    // Switch back to PeopleWithAccessMode if removing the last recipient
    if (updatedRecipients.length === 0 && isInviteMode) {
      setIsInviteMode(false);
    }
  };

  const { focusedChip, handleChipFocus, handleChipKeyDown, handleInputKeyDown } =
    useShareChipsNavigation({
      containerRef,
      shareChips: recipients.map((recipient: ShareRecipient) => ({
        email: recipient.email,
        role: selectedRole,
        userName: recipient.userName,
        avatarInitials: recipient.avatarInitials,
        status: recipient.status,
      })),
      onRemove: handleRemoveEmail,
    });

  // Reset all state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setRecipients([]);
      setEmailInput('');
      setMessage('');
      setIsInviteMode(false); // Reset mode when dialog closes
      setSelectedRole('editor'); // Also reset role to default
      setNotifyPeople(true); // Reset notify people checkbox
    }
  }, [open]);

  const handleAddEmail = async (email: string) => {
    if (!isValidEmail(email)) {
      return;
    }

    // Check if email is already in recipients
    if (recipients.some((r) => r.email === email)) {
      return;
    }

    // If ShareDialog is currently in PeopleWithAccessMode, this
    // is the first email being entered. Switch to invite mode.
    if (isPeopleWithAccessMode) {
      setIsInviteMode(true);
    }

    setRecipients((prev) => [...prev, { email, status: 'checking' }]);

    try {
      const result = await dispatch(lookupUserByEmail(email)).unwrap();

      setRecipients((prev) =>
        prev.map((r) =>
          r.email === email
            ? {
                email,
                status: result.exists ? 'exists' : 'new',
                userName: result.userName,
                uid: result.uid,
                avatarInitials: result.exists
                  ? `${result.firstName?.[0] || ''}${result.lastName?.[0] || ''}`
                  : undefined,
              }
            : r,
        ),
      );
    } catch (error) {
      console.error('Error looking up user:', error);
      setRecipients((prev) => prev.filter((r) => r.email !== email));
    }
  };

  const handleSend = async () => {
    if (!milestoneMap?.uuid) {
      console.error('No map UUID found');
      return;
    }

    try {
      // Get current pendingMapInvitations
      const currentInvitations = milestoneMap.pendingMapInvitations || [];
      const currentUsersWithAccess = milestoneMap.usersWithAccess || [];

      // Split share map recipients into new (without accounts) and existing users (with accounts)
      const existingUsers = recipients.filter((r) => r.status === 'exists' && r.uid && r.userName);
      const newUsers = recipients.filter((r) => r.status === 'new');

      // 1. Update usersWithAccess for existing users
      const updatedUsersWithAccess = [
        ...currentUsersWithAccess.filter(
          // Keep all existing users except those we're about to update
          (userAccessRole) => !existingUsers.find((u) => u.uid === userAccessRole.uid),
        ),
        // Add existing users newly shared with this map
        ...existingUsers.map(
          (userAccessRole) =>
            ({
              uid: userAccessRole.uid!,
              userName: userAccessRole.userName!,
              role: selectedRole,
            }) as UserWithAccessAndRole,
        ),
      ];

      // 2. Create new invitations for ALL recipients (both existing and new users)
      const allInvitations = recipients.map((recipient) => ({
        uid: recipient?.uid || '',
        email: recipient.email,
        invitedOn: new Date().toISOString(),
        role: selectedRole,
        status: 'pending' as const,
        createdBy: {
          uid: userProfile.uid,
          userName: userProfile.userName,
        },
      }));

      // 3. Update the map with both usersWithAccess and new (pending) invitations
      await dispatch(
        updateMilestoneMap({
          uuid: milestoneMap.uuid,
          updates: {
            usersWithAccess: updatedUsersWithAccess,
            pendingMapInvitations: [...currentInvitations, ...allInvitations],
          },
        }),
      );

      // 4. Send signup links to new users
      setSignupLinksInProgress(newUsers.map((r) => r.email));
      for (const recipient of newUsers) {
        try {
          const signUpLink = await dispatch(
            createSignUpLink({
              toEmail: recipient.email,
              accountType: 'alpha_guest',
              createdBy: userProfile.userName,
            }),
          ).unwrap();

          const result = await sendMail({
            to: recipient.email,
            subject: 'MilestoneMap.org Signup Link',
            text: `${user?.displayName || 'Someone'} has invited you to collaborate on MilestoneMap.org!

${
  message
    ? `Message from ${user?.displayName || 'Someone'}:
${message}

`
    : ''
}Click this link to create an account: ${signUpLink.url}

This invitation link will expire in 7 days.`,
            html: `<p>${user?.displayName || 'Someone'} has invited you to collaborate on MilestoneMap.org!</p>
${
  message
    ? `<p><strong>Message from ${user?.displayName || 'Someone'}:</strong></p>
<p>${message.replace(/\n/g, '<br>')}</p>`
    : ''
}
<p><a href="${signUpLink.url}">Click here to create an account</a></p>
<p>This invitation link will expire in 7 days.</p>`,
          });

          setSignupLinksSent((prev) => [...prev, { email: recipient.email, success: true }]);
        } catch (error) {
          console.error(`Error sending signup link to ${recipient.email}:`, error);
          setSignupLinksSent((prev) => [...prev, { email: recipient.email, success: false }]);
          setInvitationError(
            `Failed to send signup link to ${recipient.email}. Please try again later.`,
          );
          return;
        }
      }
      setSignupLinksInProgress([]);

      // 5. Send map invitations to all recipients who aren't already in pendingMapInvitations
      setInvitationsInProgress(recipients.map((r) => r.email));
      for (const recipient of recipients) {
        // Skip if this email is already in pendingMapInvitations
        if (currentInvitations.some((inv) => inv.email === recipient.email)) {
          continue;
        }

        try {
          const result = await sendMail({
            to: recipient.email,
            subject: `${user?.displayName || 'Someone'} invited you to collaborate on a milestone map`,
            text: `${user?.displayName || 'Someone'} has invited you to collaborate on the milestone map "${milestoneMap.title}".

${
  message
    ? `Message from ${user?.displayName || 'Someone'}:
${message}

`
    : ''
}View the map here: ${process.env.REACT_APP_BASE_URL}/${milestoneMap.owner.name}/maps/${milestoneMap.shortId}`,
            html: `<p>${user?.displayName || 'Someone'} has invited you to collaborate on the milestone map "${milestoneMap.title}".</p>
${
  message
    ? `<p><strong>Message from ${user?.displayName || 'Someone'}:</strong></p>
<p>${message.replace(/\n/g, '<br>')}</p>`
    : ''
}
<p><a href="${process.env.REACT_APP_BASE_URL}/${milestoneMap.owner.name}/maps/${milestoneMap.shortId}">Click here to view the map</a></p>`,
          });

          if (result.status !== 'sent') {
            throw new Error(result.error || 'Failed to send email');
          }
        } catch (error) {
          console.error(`Error sending map invitation to ${recipient.email}:`, error);
          setInvitationError(
            `Failed to send invitation to ${recipient.email}. Please try again later.`,
          );
          return;
        }
      }
      setInvitationsInProgress([]);

      setInvitationSuccess(`Invitations sent successfully`);

      // Clear all state and close dialog
      setRecipients([]);
      setEmailInput('');
      setMessage('');
      setSelectedRole('viewer');
      setNotifyPeople(true);
      onClose();
    } catch (error) {
      console.error('Error sending invitations:', error);
      setInvitationError('Error sending invitations. Please try again later.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="p-6 max-w-2xl" ref={containerRef}>
        <DialogTitle>Share this 30to80 Milestone Map</DialogTitle>

        {/* Success message for invitations */}
        {invitationSuccess && (
          <Toast
            message={invitationSuccess}
            variant="success"
            position="top-right"
            onClose={() => setInvitationSuccess(null)}
          />
        )}

        {/* Error message for invitations */}
        {invitationError && (
          <Toast
            message={invitationError}
            variant="error"
            position="top-right"
            onClose={() => setInvitationError(null)}
          />
        )}

        {/* Invitations in progress */}
        {(invitationsInProgress.length > 0 || signupLinksInProgress.length > 0) && (
          <div className="flex items-center gap-2 mb-4 p-2 bg-blue-50 text-blue-700 rounded-md">
            <svg
              className="animate-spin h-4 w-4 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Sending invitations...</span>
          </div>
        )}

        {isInviteMode ? (
          <InviteForm
            recipients={recipients}
            emailInput={emailInput}
            selectedRole={selectedRole}
            notifyPeople={notifyPeople}
            message={message}
            containerRef={containerRef}
            onEmailInputChange={setEmailInput}
            onAddEmail={handleAddEmail}
            onRemoveEmail={handleRemoveEmail}
            onRoleChange={setSelectedRole}
            onNotifyChange={setNotifyPeople}
            onMessageChange={setMessage}
            onSend={handleSend}
            onClose={() => setIsInviteMode(false)}
            handleChipFocus={handleChipFocus}
            handleChipKeyDown={handleChipKeyDown}
            handleInputKeyDown={handleInputKeyDown}
            focusedChip={focusedChip}
          />
        ) : (
          <div className="space-y-6">
            {/* Add EmailInputSection at the top */}
            <EmailInputSection
              recipients={recipients}
              emailInput={emailInput}
              onEmailInputChange={setEmailInput}
              onAddEmail={handleAddEmail}
              onRemoveEmail={handleRemoveEmail}
              handleChipFocus={handleChipFocus}
              handleChipKeyDown={handleChipKeyDown}
              handleInputKeyDown={handleInputKeyDown}
              focusedChip={focusedChip}
            />

            <PeopleWithAccessForm
              milestoneMap={milestoneMap}
              onClose={onClose}
              onStartInvite={() => setIsInviteMode(true)}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
